import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Helmet from 'react-helmet';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import SectionMessage from '@atlaskit/section-message';

import { addFlag } from '../actions/flag-actions';
import EmailSent from '../components/EmailSent';
import ButtonLink from '../components/ButtonLink';
import Whitebox from '../components/WhiteboxPage';
import messages from './VerifyEmailSentPage.messages';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { URLParameters } from '../types';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { CoreApplication, applicationsMetadata } from '../utilities/applications';
import Heading from '@atlaskit/heading';

export const pageId = 'welcomeSentScreen';
export const buttonClickedEventId = 'resendWelcomeSentEmail';

export const WelcomeSentSuccessPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { email, redirectType, migratingApplications, showBrowserWarning } = useAppSelector(
    state => state.welcomeSent
  );
  const location = useLocation();
  const { formatMessage } = useIntl();
  const analyticsClient = useAnalyticsClient();

  usePageViewedEvent(pageId, {
    browserWarningMessageShown: showBrowserWarning,
    redirectType,
  });

  function handleResendVerificationEmail() {
    const { token } = queryString.parse(location.search) as URLParameters;

    analyticsClient.buttonClickedEvent(pageId, buttonClickedEventId, {
      redirectType,
    });

    return fetch(`/rest/signup/welcome/resend${location.search}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(response => {
        if (response.status === 204) {
          dispatch(
            addFlag(
              'success',
              messages.verifyEmailFlagSuccessTitle,
              messages.verifyEmailFlagSuccessDesc
            )
          );
        } else {
          throw new Error(pageId + ' Invalid Token');
        }
      })
      .catch(() => {
        dispatch(
          addFlag('error', messages.verifyEmailFlagErrorTitle, messages.verifyEmailFlagErrorDesc)
        );
      });
  }

  return (
    <Whitebox
      pageId={pageId}
      header={
        <Heading as="h1" size="small">
          <FormattedMessage {...messages.migrationVerifyHeader} />
        </Heading>
      }
      whiteboxFooter={
        <ButtonLink
          id="welcome-send-validation-submit"
          spacing="none"
          className="link-color-override"
          onClick={handleResendVerificationEmail}>
          {formatMessage(messages.button)}
        </ButtonLink>
      }>
      <Helmet title={formatMessage(messages.headTitle)} />
      <EmailSent
        email={email}
        message={<MigrationVerifyMessage migratingApplications={migratingApplications} />}
        warningMessage={
          showBrowserWarning && (
            <SectionMessage testId="browser-warning" appearance="information">
              {formatMessage(messages.browserWarning)}
            </SectionMessage>
          )
        }
      />
    </Whitebox>
  );
};

export default WelcomeSentSuccessPage;

type MigratingVerifyMessageProps = {
  migratingApplications: CoreApplication[] | undefined;
};
const MigrationVerifyMessage: React.FC<MigratingVerifyMessageProps> = ({
  migratingApplications,
}) => {
  return (
    <>
      {migratingApplications !== undefined && (
        <>
          <FormattedMessage
            {...messages.resumeMigrationVerifyMessage}
            values={{
              product: migratingApplications.includes('trello')
                ? applicationsMetadata['trello'].name
                : applicationsMetadata[migratingApplications[0]]?.name ?? migratingApplications[0],
            }}
          />{' '}
        </>
      )}

      <FormattedMessage {...messages.migrationVerifyMessage} />
    </>
  );
};
